<template>
	<div v-if="isCard" class="kpi-card-mode" :class="[classColor]" @click="onContainerClick">
		<div class="text-caption text-uppercase text-medium-emphasis">{{ title }}</div>
		<div v-if="isLoading" class="kpi-card-mode__loading">
			<v-progress-circular indeterminate :color="color" :width="3" size="24"></v-progress-circular>
		</div>
		<template v-else>
			<div class="font-weight-bold main-value">
				{{ value }}
				<span v-if="unit" class="font-weight-medium main-unit">{{ unit }}</span>
			</div>
			<div v-if="showDifference && hasDifference" class="kpi-card-mode__difference" :class="classColor">
				<md-icon v-if="differenceIcon">{{ differenceIcon }}</md-icon>
				{{ difference }}
				<span v-if="differenceLabel" class="font-weight-regular text-disabled">{{ differenceLabel }}</span>
			</div>
			<div v-if="showDifference && hasDifference2" class="kpi-card-mode__difference" :class="classColor2">
				<md-icon v-if="differenceIcon2">{{ differenceIcon2 }}</md-icon>
				{{ difference2 }}
				<span v-if="differenceLabel2" class="font-weight-regular text-disabled">{{ differenceLabel2 }}</span>
			</div>
		</template>
	</div>

	<div v-else-if="isLoading" class="kpi-container" @click="onContainerClick">
		<v-progress-circular indeterminate :color="color" :width="width"></v-progress-circular>
	</div>

	<div v-else class="kpi-container" @click="onContainerClick">
		<v-progress-circular :model-value="percentage" :color="kpiColor" :width="width" :reveal="{ duration: 1800 }" :class="{ active: isSimulated }" v-ripple="{ class: 'text-primary' }">
			<v-avatar>
				<span class="title" v-if="needTitle">
					{{ title }}
				</span>
				<span :class="{ value: true, [classColor]: classColor, 'padded-value': !needTitle }">{{ value }}</span>
				<span v-if="hasObjective" class="objective">Obj : {{ objective }}</span>
				<span v-else class="objective"></span>
				<span v-if="showDifference && hasDifference" :class="{ difference: true, [classColor]: classColor }">
					<md-icon v-if="differenceIcon">{{ differenceIcon }}</md-icon>
					{{ difference }}
				</span>
				<span v-if="supportsSimulation" class="simulated">
					<v-tooltip :attach="overlayTarget" location="bottom">
						<template v-slot:activator="{ props }">
							<span v-bind="props" @click.stop="toggleSimulated">
								<md-icon>play_circle</md-icon>
							</span>
						</template>
						<span>{{ isSimulated ? t('GetRealData') : t('GetSimulatedData') }}</span>
					</v-tooltip>
				</span>
			</v-avatar>
		</v-progress-circular>

		<span v-if="annotation" class="annotation">
			<v-text-field v-if="custom" v-model="annotation" variant="outlined" density="compact" :hint="isTouchMode ? (addedToHome ? t('RemoveFromHome') : t('AddToHome')) : ''" :persistent-hint="isTouchMode" @click.stop>
				<template v-slot:append-inner>
					<v-tooltip :attach="overlayTarget" location="bottom" v-if="!addedToHome">
						<template v-slot:activator="{ props }">
							<span v-bind="props" @click.stop="removeCustomKpi">
								<md-icon class="text-danger">cancel</md-icon>
							</span>
						</template>
						<span>{{ t('Delete') }}</span>
					</v-tooltip>

					<v-tooltip :attach="overlayTarget" location="bottom">
						<template v-slot:activator="{ props }">
							<span v-bind="props" @click.stop="saveCustomKpi">
								<md-icon v-if="addedToHome" class="text-success">check_circle</md-icon>
								<md-icon v-else>check_circle</md-icon>
							</span>
						</template>
						<span>{{ addedToHome ? t('RemoveFromHome') : t('AddToHome') }}</span>
					</v-tooltip>
				</template>
			</v-text-field>
			<span v-else>{{ annotation }}</span>
		</span>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, inject, watch } from 'vue';
import { useTranslator } from '@/utils/useTranslator';
import { kpiService } from '@/services/kpiService';
import { log } from '@/utils/vueLogger.js';
import { eventBus } from '@/utils/eventBus';
import { useVisibleRefresh } from '@/utils/useVisibleRefresh';
import { deviceUtils } from '@/utils/deviceUtils';

const overlayTarget = inject('overlayTarget', null);
const t = useTranslator();
const emit = defineEmits(['kpi-apply-filters']);

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	color: {
		type: String,
		default: 'primary',
	},
	width: {
		type: Number,
		default: 12,
	},
	params: {
		type: Object,
		default: () => ({}),
	},
	annotation: {
		type: String,
		default: '',
	},
	custom: {
		type: Boolean,
		default: false,
	},
	guid: {
		type: String,
		default: null,
	},
	needTitle: {
		type: Boolean,
		default: true,
	},
	showDifference: {
		type: Boolean,
		default: true,
	},
	version: {
		type: Number,
		default: 0,
	},
	displayMode: {
		type: String,
		default: 'circle',
	},
	staticData: {
		type: Object,
		default: null,
	},
});

const isCard = computed(() => props.displayMode === 'card');

const sortedWidgets = inject('sortedWidgets', null);
const AUTO_SAVE_DELAY = 1000;

const kpi = ref({
	value: 0,
	percentage: 0,
	difference: 0,
	objective: 0,
	icon: '',
	classColor: '',
	color: 'grey-lighten-1',
});

const isTouchMode = ref(deviceUtils.isTouchMode());
const onResize = () => {
	isTouchMode.value = deviceUtils.isTouchMode();
};
const isLoading = ref(true);
const error = ref(null);
const isSimulated = ref(props.params?.simulated ?? false);
const annotation = ref(props.annotation);
let annotationSaveTimeout = null;

const supportsSimulation = computed(() => 'simulated' in (props.params ?? {}));
const percentage = computed(() => kpi.value.percentage);
const value = computed(() => kpi.value.formattedValue ?? kpi.value.value);
const difference = computed(() => kpi.value.formattedDifference ?? kpi.value.difference);
const objective = computed(() => formatKpiDisplayValue(kpi.value.formattedObjective ?? kpi.value.objective));
const hasObjective = computed(() => objective.value !== '');
const hasDifference = computed(() => difference.value !== null && difference.value !== undefined && difference.value !== '');
const numericDifference = computed(() => Number(kpi.value.difference));
function trendIcon(value) {
	if (!Number.isFinite(value)) return '';
	if (value > 0) return 'trending_up';
	if (value < 0) return 'trending_down';
	return 'trending_flat';
}
const differenceIcon = computed(() => trendIcon(numericDifference.value));

// Optional secondary difference line (card mode only) : e.g. gained codes shown under the declined count.
const difference2 = computed(() => kpi.value.formattedDifference2 ?? kpi.value.difference2);
const hasDifference2 = computed(() => difference2.value !== null && difference2.value !== undefined && difference2.value !== '');
const numericDifference2 = computed(() => Number(kpi.value.difference2));
const differenceIcon2 = computed(() => trendIcon(numericDifference2.value));
const classColor2 = computed(() => kpi.value.classColor2);
const differenceLabel2 = computed(() => kpi.value.differenceLabel2 ?? '');
const icon = computed(() => kpi.value.icon || props.params.icon || '');
const classColor = computed(() => kpi.value.classColor);
const kpiColor = computed(() => kpi.value.color || props.color);
const unit = computed(() => kpi.value.unit ?? props.staticData?.unit ?? '');
const differenceLabel = computed(() => kpi.value.differenceLabel ?? props.staticData?.differenceLabel ?? '');
const addedToHome = ref(false);

const syncAddedToHome = () => {
	if (!props.custom || !props.guid || !Array.isArray(sortedWidgets?.value)) {
		addedToHome.value = false;
		return;
	}

	addedToHome.value = sortedWidgets.value.some((widget) => (widget?.guid || widget?.id) === props.guid);
};

useVisibleRefresh('appointments-updated', getData);

onMounted(() => {
	syncAddedToHome();
	eventBus.on('custom-kpi-saved', syncAddedToHome);
	init();
	window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
	eventBus.off('custom-kpi-saved', syncAddedToHome);
	clearPendingAnnotationSave();
	window.removeEventListener('resize', onResize);
});

watch(
	() => sortedWidgets?.value,
	() => {
		syncAddedToHome();
	},
	{ deep: true }
);

watch(
	() => props.annotation,
	(newVal) => {
		if (newVal !== annotation.value) {
			annotation.value = newVal;
		}
	}
);

watch(annotation, (newVal, oldVal) => {
	if (!props.custom || !props.guid || !addedToHome.value || newVal === oldVal) {
		return;
	}

	scheduleAnnotationSave();
});

watch(
	() => props.params.simulated,
	(newVal, oldVal) => {
		if (newVal !== undefined && newVal !== oldVal) {
			isSimulated.value = newVal;
			getData();
		}
	}
);

watch(
	() => [
		props.params?.reference,
		props.params?.clusterId ?? null,
		props.params?.allPark ?? false,
		JSON.stringify(props.params?.accounts || []),
		JSON.stringify(props.params?.channels || []),
		JSON.stringify(props.params?.dlts || props.params?.deliveryLocationTypes || []),
		JSON.stringify(props.params?.productTrees || props.params?.pts || []),
		JSON.stringify(props.params?.specificities || props.params?.spes || []),
		JSON.stringify(props.params?.organisations || []),
		JSON.stringify(props.params?.companies || []),
		JSON.stringify(props.params?.deliveryLocationIds || []),
	],
	() => {
		getData();
	}
);

watch(
	() => props.version,
	(newVal, oldVal) => {
		if (newVal !== oldVal) {
			getData();
		}
	}
);

watch(
	() => props.staticData,
	(newVal) => {
		if (newVal) {
			applyStaticData(newVal);
		}
	},
	{ deep: true }
);

watch(supportsSimulation, (canSimulate) => {
	if (!canSimulate && isSimulated.value) {
		isSimulated.value = false;
		getData();
	}
});

function init() {
	// Card mode fed with externally-computed data: no service fetch needed.
	if (props.staticData) {
		applyStaticData(props.staticData);
		return;
	}

	// Delay for loading animation
	setTimeout(() => {
		getData();
	}, 500);
}

function applyStaticData(data) {
	kpi.value = {
		value: data.value ?? 0,
		formattedValue: data.formattedValue ?? null,
		percentage: data.percentage ?? 0,
		difference: data.difference ?? null,
		formattedDifference: data.formattedDifference ?? null,
		classColor: data.classColor ?? '',
		difference2: data.difference2 ?? null,
		formattedDifference2: data.formattedDifference2 ?? null,
		classColor2: data.classColor2 ?? '',
		differenceLabel2: data.differenceLabel2 ?? '',
		color: data.color ?? props.color,
		icon: data.icon ?? '',
		objective: data.objective ?? '',
		unit: data.unit ?? '',
		differenceLabel: data.differenceLabel ?? '',
	};
	isLoading.value = false;
}

function clearPendingAnnotationSave() {
	if (annotationSaveTimeout) {
		clearTimeout(annotationSaveTimeout);
		annotationSaveTimeout = null;
	}
}

function scheduleAnnotationSave() {
	clearPendingAnnotationSave();
	annotationSaveTimeout = setTimeout(() => {
		emitCustomKpiSaved(true);
	}, AUTO_SAVE_DELAY);
}

function emitCustomKpiSaved(addedToHomeState) {
	eventBus.emit('custom-kpi-saved', { guid: props.guid, params: props.params, annotation: annotation.value, addedToHome: addedToHomeState });
}

function formatKpiDisplayValue(rawValue) {
	if (rawValue === null || rawValue === undefined || rawValue === '') {
		return '';
	}

	if (typeof rawValue === 'string') {
		return rawValue;
	}

	const numericValue = Number(rawValue);
	if (!Number.isFinite(numericValue)) {
		return '';
	}

	const roundedValue = Math.round(numericValue * 100) / 100;
	const displayValue = Number.isInteger(roundedValue) ? `${roundedValue}` : roundedValue.toFixed(2);
	const suffix = kpiService.isPercentage(props.params?.reference || '') ? '%' : '';
	return `${displayValue}${suffix}`;
}

function toggleSimulated() {
	if (!supportsSimulation.value) {
		return;
	}

	isSimulated.value = !isSimulated.value;
	eventBus.emit('kpi-simulated-changed', { guid: props.guid, reference: props.params.reference, isSimulated: isSimulated.value });
	getData();
}

async function getData() {
	// Card mode fed with externally-computed data: never hit the service.
	if (props.staticData) {
		applyStaticData(props.staticData);
		return;
	}

	try {
		error.value = null;

		if (!props.params.reference) {
			log('warn', 'Kpi', 'No KPI reference provided in params.reference');
			generateFallbackData();
			return;
		}

		log('info', 'Kpi', `Loading KPI: ${props.params.reference}`);

		// Retrieve data through service
		const kpiData = await kpiService.getKpiData(props.params.reference, {
			accounts: props.params.accounts || [],
			channels: props.params.channels || [],
			clusterId: props.params.clusterId ?? null,
			dlts: props.params.dlts || [],
			deliveryLocationTypes: props.params.deliveryLocationTypes || [],
			productTrees: props.params.productTrees || props.params.pts || [],
			specificities: props.params.specificities || props.params.spes || [],
			organisations: props.params.organisations || [],
			companies: props.params.companies || [],
			simulatedMode: supportsSimulation.value ? isSimulated.value : false,
			allPark: props.params.allPark ?? true,
			deliveryLocationIds: props.params.deliveryLocationIds ?? null,
			localAppointments: props.params.localAppointments ?? null,
		});

		if (!kpiData) {
			generateFallbackData();
			return;
		}

		// Update component data
		kpi.value = {
			value: kpiData.value,
			formattedValue: kpiData.formattedValue,
			percentage: kpiData.percentage,
			difference: kpiData.difference,
			formattedDifference: kpiData.formattedDifference,
			classColor: kpiData.classColor,
			color: kpiData.color,
			icon: kpiData.icon,
			objective: kpiData.objective,
		};

		log('info', 'Kpi', `KPI ${props.params.reference} loaded successfully`);
	} catch (err) {
		log('error', 'Kpi', `Error loading KPI: ${err.message}`);
		error.value = err.message;

		// Fallback to simulated data on error
		generateFallbackData();
	} finally {
		isLoading.value = false;
	}
}

function generateFallbackData() {
	isLoading.value = true;
	const random = Math.random() * 100;
	kpi.value.value = random.toFixed(2);
	kpi.value.percentage = random;
	const diff = (Math.random() * 20 - 10).toFixed(2);
	kpi.value.difference = diff > 0 ? `+${diff}` : diff;
	kpi.value.classColor = diff > 0 ? 'positive' : diff < 0 ? 'negative' : '';
	kpi.value.icon = props.params.icon || '';
	isLoading.value = false;
}

function saveCustomKpi() {
	clearPendingAnnotationSave();
	emitCustomKpiSaved(!addedToHome.value);
}

function removeCustomKpi() {
	if (!props.guid) {
		return;
	}

	eventBus.emit('custom-kpi-removed', { guid: props.guid });
}

function onContainerClick() {
	emit('kpi-apply-filters', {
		...props.params,
		...(supportsSimulation.value ? { simulated: isSimulated.value } : {}),
	});
}
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.kpi-card-mode {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 16px;
	height: 100%;
	border-radius: 8px;
	border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
	border-left: 4px solid $color-primary;
	background: rgb(var(--v-theme-surface));

	.main-value {
		font-size: $font-size-extra-large;
		color: rgb(var(--v-theme-on-surface));

		.main-unit {
			font-size: $font-size;
		}
	}

	&__difference {
		display: flex;
		align-items: center;
		gap: 4px;

		md-icon {
			font-size: 16px;
		}

		&.positive {
			color: $color-success;
		}

		&.negative {
			color: $color-error;
		}
	}

	&__loading {
		display: flex;
		align-items: center;
		min-height: 40px;
	}
}
</style>
